import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import { Leaf, CheckCircle, ArrowRight, MapPin, Target, ArrowLeft } from "@phosphor-icons/react";
import { RIGO_AVATAR_URL } from "../../components/rigo";
import { fetchGps, reverseGeocode } from "../../lib/gps";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});
function Click({ onPick }) { useMapEvents({ click(e) { onPick(e.latlng.lat, e.latlng.lng); } }); return null; }
function Recenter({ lat, lng }) { const m = useMap(); useEffect(() => { if (lat && lng) m.setView([lat, lng], Math.max(m.getZoom(), 13)); }, [lat, lng, m]); return null; }

const STEPS = ["Phone OTP", "Location", "Details", "Review"];

export default function PublicRegister() {
  const [step, setStep] = useState(0);
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [devOtp, setDevOtp] = useState("");
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpChannels, setOtpChannels] = useState({ mobile_enabled: true, email_enabled: false, dual_enabled: false });
  const [emailForOtp, setEmailForOtp] = useState("");
  // Iter 138 — 6-digit passcode (PIN) captured at registration.
  // Login after registration uses this PIN; OTP is only for account
  // creation + PIN reset.
  const [pin, setPin] = useState("");
  const [pinConfirm, setPinConfirm] = useState("");
  const [pinSaved, setPinSaved] = useState(false);
  useEffect(() => {
    api.get("/public/otp-settings").then(r => setOtpChannels(r.data)).catch(() => {});
  }, []);
  const [loc, setLoc] = useState({ latitude: 10.0, longitude: 76.3 });
  const [details, setDetails] = useState({
    name: "", alternate_phone: "", email: "",
    address: "", landmark: "", house_no: "", house_name: "",
    state_id: "", district_id: "", lsgd_id: "", ward_id: "",
    haritha_mithram_id: "",
    property_ownership: "rented",       // Iter 220g — default RENTED
    registration_cert_required: false,  // Iter 220g — default NO
    vehicle_access: true, vehicle_type: "",
    customer_type: "residential", service_frequency: "weekly",
    primary_waste_types: [],  // Iter 73 — declared primary waste (D-BMW)
  });
  const [primaryWasteOptions, setPrimaryWasteOptions] = useState([]);
  useEffect(() => {
    // Pull D-BMW catalogue (public endpoint, no auth) so the customer
    // can tick their primary waste before filling out the rest of the
    // form. Failure is non-fatal — the section just stays empty.
    api.get("/public/waste-types?category=D-BMW")
      .then(r => setPrimaryWasteOptions(r.data || []))
      .catch(() => {});
  }, []);
  const togglePrimaryWaste = (id) => setDetails(d => ({
    ...d,
    primary_waste_types: d.primary_waste_types.includes(id)
      ? d.primary_waste_types.filter(x => x !== id)
      : [...d.primary_waste_types, id],
  }));
  const [locations, setLocations] = useState([]);
  const [done, setDone] = useState(false);
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  // Capture referral context once on first render — never change after mount.
  const refContext = useMemo(() => ({
    referred_by_code: searchParams.get("ref") || null,
    partner_id: searchParams.get("partner") || null,
  }), []);  // eslint-disable-line react-hooks/exhaustive-deps
  // Optional: fetch a friendly label for the URL so the user sees who invited them.
  const [refLabel, setRefLabel] = useState(null);
  useEffect(() => {
    if (refContext.referred_by_code) {
      api.get(`/public/customer-name/${refContext.referred_by_code}`)
        .then(r => setRefLabel({ kind: "affiliate", name: r.data.name }))
        .catch(() => setRefLabel({ kind: "affiliate", name: "a Rescigo customer" }));
    } else if (refContext.partner_id) {
      api.get(`/public/partners/${refContext.partner_id}`)
        .then(r => setRefLabel({ kind: "partner", name: r.data.partner.name, type: r.data.partner.type }))
        .catch(() => setRefLabel({ kind: "partner", name: "a Rescigo partner" }));
    }
  }, [refContext]);

  useEffect(() => {
    api.get("/public/locations").then((r) => setLocations(r.data)).catch(() => {});
  }, []);

  const states = locations.filter((x) => x.type === "state");
  const districts = locations.filter((x) => x.type === "district" && (!details.state_id || x.parent_id === details.state_id));
  const lsgds = locations.filter((x) => x.type === "lsgd" && (!details.district_id || x.parent_id === details.district_id));
  const wards = locations.filter((x) => x.type === "ward" && (!details.lsgd_id || x.parent_id === details.lsgd_id));

  // Auto-resolve Health Zone from ward → master mapping
  const [zoneInfo, setZoneInfo] = useState({ status: "idle", name: null, error: null });
  useEffect(() => {
    if (!details.ward_id) { setZoneInfo({ status: "idle", name: null, error: null }); return; }
    setZoneInfo({ status: "loading", name: null, error: null });
    api.get(`/public/ward-zone/${details.ward_id}`)
      .then(r => setZoneInfo({ status: "ok", name: r.data.zone_name, error: null, id: r.data.zone_id }))
      .catch(e => setZoneInfo({ status: "missing", name: null, error: e?.response?.data?.detail || "Not mapped" }));
  }, [details.ward_id]);

  const [dupModal, setDupModal] = useState(null);   // { message, login_url }

  const sendOtp = async () => {
    if (!phone) return toast.error("Phone required");
    try {
      const payload = { phone };
      // If email field is shown (because email or dual OTP is enabled), send it
      if (otpChannels?.email_enabled || otpChannels?.dual_enabled) {
        if (!emailForOtp) return toast.error("Email required");
        payload.email = emailForOtp;
      }
      const r = await api.post("/public/otp/request", payload);
      setOtpSent(true);
      setDevOtp(r.data.dev_otp || "");
      const ch = r.data.channels || {};
      const msg = r.data.dev_otp
        ? `OTP sent · DEV: ${r.data.dev_otp}`
        : (ch.email && ch.mobile)
          ? "OTP sent to your phone and email"
          : ch.email ? "OTP sent to your email"
          : "OTP sent";
      toast.success(msg);
    } catch (e) {
      const d = e?.response?.data?.detail;
      // Backend returns structured duplicate-phone payload
      if (d && typeof d === "object" && d.code === "phone_already_registered") {
        setDupModal({ message: d.message, login_url: d.login_url || "/me/login" });
        return;
      }
      toast.error(formatApiError(e));
    }
  };
  const verifyOtp = async () => {
    try {
      await api.post("/public/otp/verify", { phone, otp });
      setOtpVerified(true);
      toast.success("Phone verified — now create your passcode");
      // Stay on step 0; PIN form appears below. Step 1 advance
      // happens only after PIN is saved.
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Iter 138 — Save (locally hold) the 6-digit PIN before proceeding.
  // The PIN is submitted to the backend along with the rest of the
  // registration on the final Review step.
  const savePin = () => {
    if (!/^\d{6}$/.test(pin)) {
      toast.error("Passcode must be exactly 6 digits"); return;
    }
    if (pin !== pinConfirm) {
      toast.error("Both passcodes must match"); return;
    }
    // Reject trivially weak PINs (all same digit / straight sequences).
    if (/^(\d)\1{5}$/.test(pin) || pin === "123456" || pin === "654321") {
      toast.error("Please pick a less predictable passcode"); return;
    }
    setPinSaved(true);
    toast.success("Passcode saved — remember it, you'll use it to log in");
    setStep(1);
  };
  const [gpsBusy, setGpsBusy] = useState(false);
  // Iter 138 — Track whether we've successfully fetched a real GPS
  // fix. The "Continue" button on Step 1 is gated on this: user
  // MUST allow GPS to proceed. Any failure surfaces an inline
  // error + "Try again" — no manual override.
  const [gpsFixed, setGpsFixed] = useState(false);
  const [gpsError, setGpsError] = useState("");
  const grabGps = async () => {
    setGpsBusy(true);
    setGpsError("");
    try {
      const pos = await fetchGps();
      setLoc({ latitude: pos.latitude, longitude: pos.longitude });
      setGpsFixed(true);
      try {
        const g = await reverseGeocode(pos.latitude, pos.longitude);
        setDetails(d => ({
          ...d,
          address: d.address || [g.road, g.neighbourhood].filter(Boolean).join(", "),
          landmark: d.landmark || g.neighbourhood || "",
          house_no: d.house_no || g.house_number || "",
        }));
        toast.success(`Address auto-filled${g.display_name ? ` · ${g.display_name.slice(0, 40)}…` : ""}`);
      } catch {
        toast.success(`Location set${pos.accuracy ? ` · ±${Math.round(pos.accuracy)} m` : ""}`);
      }
    } catch (e) {
      setGpsError(e.message || "Could not fetch GPS");
      toast.error(e.message || "Could not fetch GPS");
    } finally {
      setGpsBusy(false);
    }
  };

  // Auto-fetch GPS + reverse-geocode the moment the user reaches Step 1 (Location).
  const [gpsAutoRan, setGpsAutoRan] = useState(false);
  useEffect(() => {
    if (step !== 1 || gpsAutoRan) return;
    setGpsAutoRan(true);
    grabGps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Iter 138 — Step 2 (Details) also re-attempts reverse-geocode using
  // the coordinates locked in Step 1, and auto-selects state / district
  // / LSGD in the location dropdowns based on the returned admin
  // names. Fields the user has already typed are NOT overwritten.
  const [detailsAutoRan, setDetailsAutoRan] = useState(false);
  useEffect(() => {
    if (step !== 2 || detailsAutoRan) return;
    setDetailsAutoRan(true);
    if (!loc.latitude || !loc.longitude) return;
    (async () => {
      try {
        const g = await reverseGeocode(loc.latitude, loc.longitude);
        // Utility: find a location row (state/district/lsgd) by fuzzy
        // name match within its parent scope.
        const findLoc = (type, needle, parentId = null) => {
          if (!needle) return null;
          const nk = needle.toLowerCase().replace(/[^a-z]/g, "");
          const pool = locations.filter(x =>
            x.type === type && (parentId ? x.parent_id === parentId : true));
          // Exact (case-insensitive) match first, then contains.
          return (
            pool.find(x => x.name.toLowerCase() === needle.toLowerCase()) ||
            pool.find(x => x.name.toLowerCase().replace(/[^a-z]/g, "") === nk) ||
            pool.find(x => x.name.toLowerCase().includes(needle.toLowerCase())) ||
            null
          );
        };
        const stateRow = findLoc("state", g.state);
        const districtRow = stateRow
          ? findLoc("district", g.city || g.state, stateRow.id) ||
            findLoc("district", g.county, stateRow.id)
          : null;
        // LSGD is trickier — try city/town/village first.
        const lsgdCandidate = g.city || g.neighbourhood;
        const lsgdRow = districtRow && lsgdCandidate
          ? findLoc("lsgd", lsgdCandidate, districtRow.id)
          : null;
        setDetails(d => ({
          ...d,
          address: d.address || [g.road, g.neighbourhood].filter(Boolean).join(", "),
          landmark: d.landmark || g.neighbourhood || "",
          house_no: d.house_no || g.house_number || "",
          state_id: d.state_id || stateRow?.id || "",
          district_id: d.district_id || districtRow?.id || "",
          lsgd_id: d.lsgd_id || lsgdRow?.id || "",
        }));
        const hit = [
          stateRow ? "state" : null,
          districtRow ? "district" : null,
          lsgdRow ? "LSGD" : null,
        ].filter(Boolean);
        if (hit.length) {
          toast.success(`Auto-selected ${hit.join(", ")} from your location`);
        }
      } catch {
        // Silent — user can still fill manually.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, locations]);
  const submit = async () => {
    if (!details.lsgd_id) return toast.error("LSGD is required");
    if (!details.ward_id) return toast.error("Ward is required");
    // Iter 220 — Haritha Mithram ID + Health Zone mapping are no longer
    // hard blockers. Admin fills / maps them during verification. The
    // Hygiene module stays locked until the account is verified.
    if (details.registration_cert_required === null) {
      return toast.error("Please indicate whether you need a registration certificate for licence");
    }
    try {
      await api.post("/public/register", {
        phone, otp,
        pin: pin || null,  // Iter 138 — customer passcode for later logins
        name: details.name,
        alternate_phone: details.alternate_phone || null,
        email: details.email || null,
        latitude: Number(loc.latitude), longitude: Number(loc.longitude),
        address: details.address, landmark: details.landmark,
        house_no: details.house_no, house_name: details.house_name,
        state_id: details.state_id || null,
        district_id: details.district_id || null,
        lsgd_id: details.lsgd_id || null,
        ward_id: details.ward_id || null,
        haritha_mithram_id: (details.haritha_mithram_id || "").trim() || null,
        property_ownership: details.property_ownership || "owner",
        registration_cert_required: details.registration_cert_required,
        vehicle_access: !!details.vehicle_access,
        vehicle_type: details.vehicle_type || null,
        waste_types: [],
        primary_waste_types: details.primary_waste_types,
        customer_type: details.customer_type,
        service_frequency: details.service_frequency,
        // Referral / Partner attribution from URL query
        referred_by_code: refContext.referred_by_code,
        partner_id: refContext.partner_id,
      });
      setDone(true);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F6F2] p-6">
        <div className="max-w-lg w-full bg-white border border-[#E5E2D9] rounded-md p-10 text-center">
          <div className="w-16 h-16 rounded-full bg-[#D4E0D1] mx-auto flex items-center justify-center">
            <CheckCircle size={32} color="#2D5A27" weight="fill" />
          </div>
          <h1 className="font-heading text-3xl mt-5">Registration received</h1>
          <p className="text-[#5C5A55] mt-3">
            Your request is now pending admin approval. You will be notified on
            <span className="font-mono ml-1 text-[#1A1A1A]">{phone}</span> once verified.
          </p>
          <Link to="/me/login" className="inline-block mt-8 px-5 h-11 leading-[2.75rem] bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium transition-colors">
            Go to login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-white font-body flex flex-col">
      <div className="mx-auto w-full max-w-[460px] min-h-[100dvh] bg-white shadow-[0_0_60px_rgba(220,38,38,0.06)] relative flex flex-col overflow-x-hidden">

        {/* Red hero with Rigo mascot */}
        <div className="relative bg-gradient-to-br from-[#DC2626] via-[#B91C1C] to-[#7F1D1D] text-white pt-8 pb-16 px-5 overflow-hidden">
          <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-white/15 blur-2xl"/>
          <div className="absolute -bottom-20 -left-16 w-56 h-56 rounded-full bg-black/25 blur-2xl"/>

          <div className="relative flex items-center justify-between mb-6">
            <Link to="/" className="flex items-center gap-2">
              <img src="/brand/rescigo-mark-128.png" alt="Rescigo"
                    className="w-10 h-10 rounded-xl bg-white p-1 shadow-md"/>
              <div className="font-display font-black text-[16px] leading-none">
                rescigo <span className="text-[#FECACA]">health</span>
              </div>
            </Link>
            {/* Iter 220g — Back button moved inside the form footer for
                each step so it doesn't crowd the header. */}
          </div>

          <div className="relative flex items-start gap-2">
            <div className="flex-1 min-w-0 pt-2">
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/80 font-bold mb-2">
                Step {step + 1} of {STEPS.length}
              </div>
              <h1 className="font-display font-black text-[22px] leading-[1.1] tracking-tight">
                {step === 0 ? "Let's get started" :
                 step === 1 ? "Where are you?" :
                 step === 2 ? "About you" :
                 "Almost done!"}
              </h1>
              <p className="text-[11px] text-white/85 mt-1.5 max-w-[160px] leading-snug">
                {step === 0 ? "Verify your mobile to begin." :
                 step === 1 ? "Confirm your pickup area." :
                 step === 2 ? "Tell us about your household." :
                 "Review & submit."}
              </p>
            </div>
            <img src={RIGO_AVATAR_URL} alt="Rigo"
                  className="w-[290px] h-[320px] object-contain rigo-wave shrink-0 -mr-6 -mt-10 -mb-14 relative z-10"
                  style={{ filter: "drop-shadow(0 22px 32px rgba(0,0,0,0.35))" }}/>
          </div>

          {refLabel && (
            <div className="relative mt-4 p-3 rounded-2xl bg-white/10 backdrop-blur border border-white/20 flex items-start gap-2" data-testid="ref-banner">
              <Leaf size={14} weight="fill" className="text-[#FECACA] shrink-0 mt-0.5"/>
              <div className="text-[12px] flex-1">
                <div className="font-semibold text-white">
                  {refLabel.kind === "partner"
                    ? <>Via <span className="text-[#FECACA]">{refLabel.name}</span>{refLabel.type ? ` (${refLabel.type})` : ""}</>
                    : <>Invited by <span className="text-[#FECACA]">{refLabel.name}</span></>}
                </div>
                <div className="text-[11px] text-white/75 mt-0.5">Referral will be tagged automatically.</div>
              </div>
            </div>
          )}
        </div>

        {/* Progress bar */}
        <div className="px-5 -mt-8 relative z-10">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-[0_20px_50px_rgba(220,38,38,0.10)] px-4 py-3">
            <div className="flex items-center gap-1.5">
              {STEPS.map((s, i) => (
                <div key={s} className="flex-1">
                  <div className={`h-1.5 rounded-full transition-colors ${
                    i <= step ? "bg-[#DC2626]" : "bg-gray-200"
                  }`}/>
                  <div className={`text-[9px] uppercase tracking-widest font-bold mt-1.5 text-center ${
                    i === step ? "text-[#DC2626]" : i < step ? "text-gray-500" : "text-gray-300"
                  }`}>{s}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Form card */}
        <div className="px-5 mt-4 flex-1">
          <div className="bg-white rounded-3xl border border-gray-100 shadow-[0_8px_24px_rgba(0,0,0,0.04)] p-5">
          {step === 0 && (
            <div className="space-y-4" data-testid="reg-step-otp">
              <div>
                <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">Phone</label>
                <input data-testid="reg-phone" value={phone} onChange={(e) => setPhone(e.target.value)}
                       placeholder="e.g. 9876543210"
                       disabled={otpVerified}
                       className="w-full h-11 px-4 border border-[#E5E2D9] rounded-md font-mono disabled:bg-[#F7F6F2]" />
              </div>
              {(otpChannels.email_enabled || otpChannels.dual_enabled) && (
                <div>
                  <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">
                    Email {otpChannels.dual_enabled ? "(OTP also sent here)" : ""}
                  </label>
                  <input data-testid="reg-email" type="email" value={emailForOtp}
                          onChange={(e) => setEmailForOtp(e.target.value)}
                          placeholder="you@example.com"
                          disabled={otpVerified}
                          className="w-full h-11 px-4 border border-[#E5E2D9] rounded-md disabled:bg-[#F7F6F2]" />
                </div>
              )}
              {!otpSent ? (
                <button data-testid="reg-send-otp" onClick={sendOtp}
                        className="w-full h-11 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium">
                  Send OTP
                </button>
              ) : !otpVerified ? (
                <>
                  <div>
                    <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">
                      Enter the 6-digit OTP
                    </label>
                    {/* Iter 138 — Bigger OTP input for easier reading on mobile. */}
                    <input data-testid="reg-otp" value={otp}
                           onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                           inputMode="numeric" maxLength={6} autoFocus
                           placeholder="••••••"
                           className="w-full h-16 px-4 border-2 border-[#2D5A27] rounded-md font-mono text-3xl sm:text-4xl tracking-[0.5em] text-center focus:outline-none focus:ring-2 focus:ring-[#2D5A27]/40" />
                    {devOtp ? (
                      <div className="mt-2 p-3 rounded-md bg-[#F9E4B7] border border-[#E5C978] text-center">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-[#7A5A11] font-semibold">Demo mode · your OTP is</div>
                        <div className="mt-1 font-mono text-3xl sm:text-4xl tracking-[0.4em] text-[#7A5A11] font-bold"
                             data-testid="reg-dev-otp">{devOtp}</div>
                        <div className="text-[10px] text-[#7A5A11] mt-1 opacity-75">In production this arrives via SMS.</div>
                      </div>
                    ) : (
                      <div className="text-xs text-[#8A8780] mt-1 text-center">
                        Check the OTP sent to your phone{otpChannels.email_enabled ? " and email" : ""}.
                      </div>
                    )}
                  </div>
                  <button data-testid="reg-verify-otp" onClick={verifyOtp}
                          disabled={otp.length !== 6}
                          className="w-full h-11 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-50 text-white rounded-md text-sm font-medium">
                    Verify OTP
                  </button>
                </>
              ) : (
                // Iter 138 — Passcode capture block, shown ONLY after
                // OTP is verified. User must set a 6-digit PIN before
                // we move on to the map.
                <div className="space-y-4 pt-2 border-t border-[#E5E2D9]" data-testid="reg-pin-block">
                  <div className="flex items-start gap-2 bg-[#EFF3EE] border border-[#D4E0D1] rounded-md p-3">
                    <CheckCircle size={16} weight="fill" color="#2D5A27" className="shrink-0 mt-0.5"/>
                    <div>
                      <div className="text-[13px] font-medium text-[#1A3317]">Phone verified</div>
                      <div className="text-[11px] text-[#5C5A55] mt-0.5">
                        Now create a 6-digit passcode — you&apos;ll use it every time you sign in.
                        OTP is only for creating your account.
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">
                      Create passcode (6 digits)
                    </label>
                    <input data-testid="reg-pin"
                           type="tel" inputMode="numeric" maxLength={6} autoFocus
                           value={pin}
                           onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                           placeholder="••••••"
                           className="w-full h-16 px-4 border-2 border-[#2D5A27] rounded-md font-mono text-3xl sm:text-4xl tracking-[0.5em] text-center focus:outline-none focus:ring-2 focus:ring-[#2D5A27]/40"/>
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">
                      Confirm passcode
                    </label>
                    <input data-testid="reg-pin-confirm"
                           type="tel" inputMode="numeric" maxLength={6}
                           value={pinConfirm}
                           onChange={(e) => setPinConfirm(e.target.value.replace(/\D/g, "").slice(0, 6))}
                           placeholder="••••••"
                           className="w-full h-14 px-4 border border-[#E5E2D9] rounded-md font-mono text-2xl tracking-[0.4em] text-center focus:outline-none focus:border-[#2D5A27]"/>
                    {pin && pinConfirm && pin !== pinConfirm && (
                      <div className="text-[11px] text-[#C25D38] mt-1">Passcodes don&apos;t match yet</div>
                    )}
                  </div>
                  <div className="text-[11px] text-[#5C5A55] leading-relaxed bg-[#F7F6F2] rounded p-2">
                    <b>Tip:</b> Avoid predictable codes like <code>123456</code> or your birth year.
                    You can reset it anytime via OTP.
                  </div>
                  <button data-testid="reg-save-pin" onClick={savePin}
                          disabled={pin.length !== 6 || pin !== pinConfirm}
                          className="w-full h-11 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-50 text-white rounded-md text-sm font-medium inline-flex items-center justify-center gap-2">
                    Save passcode & continue <ArrowRight size={14} weight="bold"/>
                  </button>
                </div>
              )}
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4" data-testid="reg-step-loc">
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] flex items-center gap-2">
                <MapPin size={12} /> We need your location to schedule pickups
              </div>
              {/* Iter 138 — GPS gate: user MUST allow location. No
                  Continue button until we have a real fix. */}
              {!gpsFixed && !gpsBusy && (
                <div className="rounded-md border-2 border-[#F5C518] bg-[#FFF9E3] p-4 space-y-3"
                     data-testid="reg-gps-required">
                  <div className="flex items-start gap-2">
                    <Target size={18} weight="fill" color="#7A5A11" className="shrink-0 mt-0.5"/>
                    <div>
                      <div className="text-sm font-medium text-[#7A5A11]">
                        Enable location on your device
                      </div>
                      <div className="text-[12px] text-[#7A5A11]/85 mt-1 leading-relaxed">
                        {gpsError
                          ? <>Couldn&apos;t get your location. <b>{gpsError}</b> Please turn on location for your browser and try again.</>
                          : "We use your GPS to auto-fill your address and schedule the right route. Nothing else is captured."}
                      </div>
                    </div>
                  </div>
                  <button type="button" onClick={grabGps}
                          data-testid="reg-gps-retry"
                          className="w-full h-11 bg-[#7A5A11] hover:bg-[#5F4610] text-white rounded-md text-sm font-medium inline-flex items-center justify-center gap-2">
                    <Target size={14}/>{gpsError ? "Try again" : "Allow & fetch my location"}
                  </button>
                </div>
              )}
              {gpsBusy && (
                <div className="rounded-md border border-[#E5E2D9] bg-[#F7F6F2] p-6 text-center"
                     data-testid="reg-gps-busy">
                  <div className="inline-flex items-center gap-2 text-sm text-[#5C5A55]">
                    <Target size={16} className="animate-pulse text-[#2D5A27]"/>
                    Locating you… this can take a few seconds.
                  </div>
                </div>
              )}
              {gpsFixed && (
                <>
                  <div className="rounded-md border border-[#D4E0D1] bg-[#EFF3EE] p-3 flex items-start gap-2"
                       data-testid="reg-gps-ok">
                    <CheckCircle size={16} weight="fill" color="#2D5A27" className="shrink-0 mt-0.5"/>
                    <div className="text-[12px] text-[#1A3317]">
                      <b>Location captured.</b> Fine-tune by tapping on the map below if needed.
                      <div className="text-[10px] font-mono text-[#5C5A55] mt-0.5">
                        {Number(loc.latitude).toFixed(5)}, {Number(loc.longitude).toFixed(5)}
                      </div>
                    </div>
                  </div>
                  <div className="h-64 border border-[#E5E2D9] rounded-md overflow-hidden">
                    <MapContainer center={[loc.latitude, loc.longitude]} zoom={16} style={{ height: "100%", width: "100%" }}>
                      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"/>
                      <Click onPick={(lat, lng) => setLoc({ latitude: lat, longitude: lng })} />
                      <Recenter lat={loc.latitude} lng={loc.longitude} />
                      <Marker position={[loc.latitude, loc.longitude]} />
                    </MapContainer>
                  </div>
                  <button type="button" onClick={grabGps} disabled={gpsBusy}
                          data-testid="pr-gps-btn"
                          className="h-9 px-4 border border-[#E5E2D9] rounded-md text-xs inline-flex items-center gap-2 disabled:opacity-60 hover:bg-[#F7F6F2]">
                    <Target size={12} /> Re-fetch my current location
                  </button>
                  <div className="flex justify-between items-center">
                    <button type="button" onClick={() => setStep(0)}
                            data-testid="reg-back-btn-1"
                            className="h-11 px-4 border border-[#E5E2D9] rounded-md text-xs font-bold text-[#5C5A55] inline-flex items-center gap-1.5 hover:bg-[#F7F6F2]">
                      <ArrowLeft size={12} weight="bold"/>Back
                    </button>
                    <button onClick={() => setStep(2)}
                            data-testid="reg-loc-continue"
                            className="h-11 px-5 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium inline-flex items-center gap-2">
                      Continue <ArrowRight size={14} weight="bold"/>
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5" data-testid="reg-step-details">
              {/* Iter 73 — dedicated "Your primary waste" section at the
                  top of the details step. Multi-select chips from the
                  D-BMW category. Optional (customer can skip) but
                  encouraged with a helpful subhead. */}
              <div className="bg-[#EFF3EE] border border-[#D4E0D1] rounded-md p-4"
                    data-testid="reg-primary-waste-section">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#2D5A27] font-semibold">
                  Your primary waste
                </div>
                <div className="text-[13px] text-[#5C5A55] mt-0.5">
                  Which of these do you regularly dispose of? Pick all that apply — this helps us schedule the right bags for you.
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {primaryWasteOptions.length === 0 && (
                    <div className="text-[11px] text-[#8A8780] italic">Loading options…</div>
                  )}
                  {primaryWasteOptions.map(o => {
                    const on = details.primary_waste_types.includes(o.id);
                    return (
                      <button key={o.id} type="button"
                              onClick={() => togglePrimaryWaste(o.id)}
                              data-testid={`reg-primary-waste-${o.id}`}
                              aria-pressed={on}
                              className={`h-9 px-3 rounded-full border text-xs font-medium transition-colors ${
                                on
                                  ? "bg-[#2D5A27] text-white border-[#2D5A27]"
                                  : "bg-white text-[#1A1A1A] border-[#E5E2D9] hover:border-[#2D5A27]"
                              }`}>
                        {on ? "✓ " : ""}{o.name}
                      </button>
                    );
                  })}
                </div>
                {details.primary_waste_types.length > 0 && (
                  <div className="mt-2 text-[10px] uppercase tracking-[0.18em] text-[#2D5A27]"
                        data-testid="reg-primary-waste-count">
                    {details.primary_waste_types.length} selected
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
              <TxtField label="Full name" value={details.name} onChange={(v) => setDetails(d => ({ ...d, name: v }))} required />
              <TxtField label="Alt phone" value={details.alternate_phone} onChange={(v) => setDetails(d => ({ ...d, alternate_phone: v }))} />
              <TxtField label="Email" type="email" value={details.email} onChange={(v) => setDetails(d => ({ ...d, email: v }))} />
              <TxtField label="House no" value={details.house_no} onChange={(v) => setDetails(d => ({ ...d, house_no: v }))} />
              <TxtField label="House name" value={details.house_name} onChange={(v) => setDetails(d => ({ ...d, house_name: v }))} />
              <TxtField label="Landmark" value={details.landmark} onChange={(v) => setDetails(d => ({ ...d, landmark: v }))} />
              <div className="col-span-2">
                <TxtField label="Address" value={details.address} onChange={(v) => setDetails(d => ({ ...d, address: v }))} />
              </div>

              <SelField label="State" testid="reg-state" value={details.state_id}
                        onChange={(v) => setDetails(d => ({ ...d, state_id: v, district_id: "", lsgd_id: "", ward_id: "" }))}
                        options={[{ value: "", label: "— select —" }, ...states.map(s => ({ value: s.id, label: s.name }))]} />
              <SelField label="District" testid="reg-district" value={details.district_id}
                        disabled={!details.state_id}
                        onChange={(v) => setDetails(d => ({ ...d, district_id: v, lsgd_id: "", ward_id: "" }))}
                        options={[{ value: "", label: details.state_id ? "— select —" : "Select state first" }, ...districts.map(s => ({ value: s.id, label: s.name }))]} />
              <SelField label="LSGD" testid="reg-lsgd" value={details.lsgd_id}
                        disabled={!details.district_id}
                        onChange={(v) => setDetails(d => ({ ...d, lsgd_id: v, ward_id: "" }))}
                        options={[{ value: "", label: details.district_id ? "— select —" : "Select district first" }, ...lsgds.map(s => ({ value: s.id, label: `${s.name}${s.lsgd_type ? ` (${s.lsgd_type.replace("_", " ")})` : ""}` }))]} />
              <SelField label="Ward" testid="reg-ward" value={details.ward_id}
                        disabled={!details.lsgd_id}
                        onChange={(v) => setDetails(d => ({ ...d, ward_id: v }))}
                        options={[{ value: "", label: details.lsgd_id ? "— select —" : "Select LSGD first" }, ...wards.map(w => ({ value: w.id, label: `${w.ward_no ? `#${w.ward_no} · ` : ""}${w.name}` }))]} />

              {/* Auto-derived Health Zone (read-only) */}
              <div className="sm:col-span-2 mt-1" data-testid="reg-zone-block">
                <label className="text-[10px] uppercase tracking-[0.18em] text-[#8A8780] block mb-1.5">Health Zone (auto)</label>
                <div className={`h-10 px-3 inline-flex items-center gap-2 rounded-md border w-full text-sm ${
                  zoneInfo.status === "ok" ? "bg-[#EFF3EE] border-[#2D5A27] text-[#2D5A27]" :
                  zoneInfo.status === "missing" ? "bg-[#FFF8E6] border-[#B58412] text-[#7A5A11]" :
                  "bg-[#F7F6F2] border-[#E5E2D9] text-[#8A8780]"
                }`} data-testid="reg-zone-display">
                  {!details.ward_id && <span>Select a ward first</span>}
                  {details.ward_id && zoneInfo.status === "loading" && <span>Resolving…</span>}
                  {zoneInfo.status === "ok" && <span><strong>{zoneInfo.name}</strong> — auto-assigned</span>}
                  {zoneInfo.status === "missing" && (
                    <span>Not mapped yet — admin will assign this during verification.</span>
                  )}
                </div>
              </div>

              <TxtField label="Haritha Mithram ID (optional)" testid="reg-haritha"
                        value={details.haritha_mithram_id}
                        onChange={(v) => setDetails(d => ({ ...d, haritha_mithram_id: v }))} />

              <div className="col-span-2 pt-2 border-t border-[#E5E2D9]">
                <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] mb-2">Property ownership *</div>
                <div className="flex gap-2">
                  {[
                    { v: "owner",  l: "Owner" },
                    { v: "rented", l: "Rented" },
                  ].map((x) => (
                    <button type="button" key={x.v}
                            onClick={() => setDetails(d => ({ ...d, property_ownership: x.v }))}
                            data-testid={`reg-prop-${x.v}`}
                            className={`flex-1 h-10 rounded-md text-sm border ${details.property_ownership === x.v ? "bg-[#D4E0D1] border-[#2D5A27] text-[#1A3317]" : "border-[#E5E2D9]"}`}>
                      {x.l}
                    </button>
                  ))}
                </div>
              </div>

              <div className="col-span-2">
                <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] mb-2">
                  Do you need a Rescigo registration certificate for your licence? *
                </div>
                <div className="flex gap-2">
                  {[
                    { v: true,  l: "Yes" },
                    { v: false, l: "No" },
                  ].map((x) => (
                    <button type="button" key={String(x.v)}
                            onClick={() => setDetails(d => ({ ...d, registration_cert_required: x.v }))}
                            data-testid={`reg-regcert-${x.v ? "yes" : "no"}`}
                            className={`flex-1 h-10 rounded-md text-sm border ${details.registration_cert_required === x.v ? "bg-[#D4E0D1] border-[#2D5A27] text-[#1A3317]" : "border-[#E5E2D9]"}`}>
                      {x.l}
                    </button>
                  ))}
                </div>
              </div>

              <SelField label="Customer type" testid="reg-ctype" value={details.customer_type} onChange={(v) => setDetails(d => ({ ...d, customer_type: v }))}
                        options={[{ value: "residential", label: "Residential" }, { value: "commercial", label: "Commercial" }]} />
              <SelField label="Pickup frequency" value={details.service_frequency} onChange={(v) => setDetails(d => ({ ...d, service_frequency: v }))}
                        options={[{ value: "weekly", label: "Weekly" }, { value: "bi-weekly", label: "Bi-weekly" }, { value: "monthly", label: "Monthly" }]} />

              <div className="col-span-2 pt-2 border-t border-[#E5E2D9]">
                <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] mb-2">Vehicle access to premises</div>
                <div className="flex gap-2 mb-3">
                  {[{ v: true, l: "Yes, vehicle can reach" }, { v: false, l: "No vehicle access" }].map((x) => (
                    <button type="button" key={String(x.v)}
                            onClick={() => setDetails((d) => ({ ...d, vehicle_access: x.v, vehicle_type: x.v ? d.vehicle_type : "" }))}
                            data-testid={`reg-vacc-${x.v ? "yes" : "no"}`}
                            className={`flex-1 h-10 rounded-md text-sm border ${details.vehicle_access === x.v ? "bg-[#D4E0D1] border-[#2D5A27] text-[#1A3317]" : "border-[#E5E2D9]"}`}>
                      {x.l}
                    </button>
                  ))}
                </div>
                {details.vehicle_access && (
                  <SelField label="Vehicle type that can reach" testid="reg-vtype" value={details.vehicle_type} onChange={(v) => setDetails(d => ({ ...d, vehicle_type: v }))}
                            options={[
                              { value: "", label: "— select —" },
                              { value: "2_wheeler", label: "2 Wheeler" },
                              { value: "3_wheeler", label: "3 Wheeler" },
                              { value: "4_wheeler", label: "4 Wheeler" },
                              { value: "6_wheeler_truck", label: "6 Wheeler Truck" },
                            ]} />
                )}
              </div>

              <div className="col-span-2 flex justify-between items-center">
                <button type="button" onClick={() => setStep(1)}
                        data-testid="reg-back-btn-2"
                        className="h-11 px-4 border border-[#E5E2D9] rounded-md text-xs font-bold text-[#5C5A55] inline-flex items-center gap-1.5 hover:bg-[#F7F6F2]">
                  <ArrowLeft size={12} weight="bold"/>Back
                </button>
                <button onClick={() => setStep(3)} disabled={!details.name}
                        className="h-11 px-5 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50">
                  Review <ArrowRight size={14} weight="bold"/>
                </button>
              </div>
              </div>{/* /grid-cols-2 (Iter 73 wraps the whole thing in space-y-5) */}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4" data-testid="reg-step-review">
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780]">Review</div>
              <div className="font-heading text-lg">Confirm your details</div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Row k="Name" v={details.name} />
                <Row k="Phone" v={phone} />
                <Row k="Address" v={details.address} />
                <Row k="GPS" v={`${Number(loc.latitude).toFixed(5)}, ${Number(loc.longitude).toFixed(5)}`} />
                <Row k="Type" v={details.customer_type} />
                <Row k="Frequency" v={details.service_frequency} />
              </div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setStep(2)}
                        data-testid="reg-back-btn-3"
                        className="h-11 px-4 border border-[#E5E2D9] rounded-md text-xs font-bold text-[#5C5A55] inline-flex items-center gap-1.5 hover:bg-[#F7F6F2]">
                  <ArrowLeft size={12} weight="bold"/>Back
                </button>
                <button data-testid="reg-submit" onClick={submit}
                        className="flex-1 h-11 bg-[#C25D38] hover:bg-[#A74E2F] text-white rounded-md text-sm font-medium inline-flex items-center justify-center gap-2">
                  Submit registration <ArrowRight size={14} weight="bold"/>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Existing customer login — always visible at bottom */}
        <div className="px-5 py-6 mt-2">
          <div className="text-center text-[12px] text-gray-500">
            Already have an account?{" "}
            <Link to="/me/login" data-testid="reg-goto-login"
                  className="font-bold text-[#DC2626] hover:text-[#B91C1C] underline underline-offset-2">
              Log in here
            </Link>
          </div>
        </div>
      </div>

      {/* Duplicate-phone modal */}
      {dupModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="dup-phone-modal">
          <div className="bg-white rounded-md max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#FDECE6] flex items-center justify-center flex-none">
                <span className="text-[#C25D38] font-heading text-xl">!</span>
              </div>
              <h3 className="font-heading text-xl">Account already exists</h3>
            </div>
            <p className="text-sm text-[#5C5A55] mt-3 leading-relaxed">
              {dupModal.message}
            </p>
            <div className="mt-6 grid grid-cols-2 gap-3">
              <Link to={dupModal.login_url || "/me/login"} data-testid="dup-go-login"
                    className="inline-flex items-center justify-center h-11 px-4 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium">
                Go to login
              </Link>
              <button data-testid="dup-different-number"
                       onClick={() => { setPhone(""); setOtp(""); setOtpSent(false); setDupModal(null); }}
                       className="inline-flex items-center justify-center h-11 px-4 border border-[#E5E2D9] hover:border-[#2D5A27] rounded-md text-sm font-medium text-[#1A1A1A]">
                Register with a different number
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  </div>
  );
}

const TxtField = ({ label, value, onChange, type = "text", required, testid }) => (
  <div>
    <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">{label}</label>
    <input type={type} required={required} value={value || ""} onChange={(e) => onChange(e.target.value)}
           data-testid={testid}
           className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md" />
  </div>
);
const SelField = ({ label, value, onChange, options, disabled, testid }) => (
  <div>
    <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">{label}</label>
    <select value={value || ""} onChange={(e) => onChange(e.target.value)}
            disabled={disabled} data-testid={testid}
            className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md bg-white disabled:bg-[#F7F6F2] disabled:opacity-60">
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);
const Row = ({ k, v }) => (
  <div className="bg-[#F7F6F2] border border-[#E5E2D9] rounded p-3">
    <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780]">{k}</div>
    <div className="mt-0.5 text-[#1A1A1A]">{v || "—"}</div>
  </div>
);
